// Unpublished Work © 2023-2024 Deere & Company.

import { AxiosResponse } from "axios";
import {
  BackOrdersDetailsResponse,
  OrderCancelResponse,
  OrderCreationResponse,
  OrderLineCancelBody,
  OrderLineRouteToDealerBody,
  OrderReviewResponse2,
  OrderUpdateResponse,
  PendingOrder,
  PendingOrdersGroup,
} from "../../pages/orders/types";
import { BlobResponse } from "../../utils/downloadFile";
import axios from "../api";
import {
  AllOrdersRequestBody,
  IOrderApi,
  Order,
  OrderLineItemStatusCountResponse,
  OrderLineRequestBody,
  OrderLineStatusCountRequest,
  OrderProcessRequest,
  OrdersByLineItemResponse,
  SearchOrderResponse,
} from "./orderInterfaces";
import { OrderCreateRequest } from "./types";

const EXCEL_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const OrderApi: IOrderApi = {
  create: (
    url: string,
    payload: OrderCreateRequest,
    token: string,
    lang: string
  ): Promise<OrderCreationResponse> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "create", lang, client: "UI" },
      })
      .then((response: any) => response.data),

  cancelOrder: (
    url: string,
    payload: OrderCreateRequest,
    token: string,
    lang: string
  ): Promise<OrderCancelResponse> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "cancel", lang, client: "UI" },
      })
      .then((response: any) => response.data),
  getOrderLinesStatusCountV2: (
    url: string,
    token: string,
    body: OrderLineStatusCountRequest,
    signal?: AbortSignal
  ) =>
    axios
      .postWithRetries<OrderLineItemStatusCountResponse>(
        `${url}/v2/orders/lines/search/count`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
          ...(signal ? { signal } : {}),
        }
      )
      .then((res) => res.data),
  update: (
    url: string,
    payload: OrderProcessRequest,
    token: string,
    lang: string
  ): Promise<OrderUpdateResponse> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "update", lang, client: "UI" },
      })
      .then((response: any) => response.data),

  submit: (
    url: string,
    payload: OrderProcessRequest,
    token: string,
    lang: string
  ): Promise<OrderUpdateResponse> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "submit", lang, client: "UI" },
      })
      .then((response: any) => response.data),

  review: (
    url: string,
    payload: OrderProcessRequest,
    token: string,
    lang: string
  ): Promise<OrderReviewResponse2> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "review", lang, client: "UI" },
      })
      .then((response: any) => response.data),

  lineReview: (
    url: string,
    payload: OrderProcessRequest,
    token: string,
    lang: string
  ): Promise<OrderReviewResponse2> =>
    axios
      .postWithRetries(`${url}/v1/orders/process`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userAction: "line_review", lang, client: "UI" },
      })
      .then((response: any) => response.data),

  processOrder: async (
    orderServiceUrl: string,
    processOrderRequest: OrderProcessRequest,
    userAction: string,
    token: string,
    lang?: string
  ): Promise<Order> => {
    const orderResponse = await axios
      .postWithRetries(
        `${orderServiceUrl}/v1/orders/process`,
        {
          ...processOrderRequest,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { userAction, lang, client: "UI" },
        }
      )
      .then((response: any) => response.data);

    return { ...orderResponse } as Order;
  },

  summary: (
    url: string,
    token: string,
    orderNumber: string,
    accountId: string,
    lang?: string
  ): Promise<Order> =>
    axios
      .getWithRetries<Order>(
        `${url}/v1/orders/summary/${orderNumber}?accountId=${accountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          ...(lang && { params: { lang } }),
        }
      )
      .then((res) => res.data),

  getPendingOrders: (url, token, accountId, pendingOrdersGroup) =>
    axios
      .getWithRetries<PendingOrder[]>(
        `${url}/v1/orders/pending/${pendingOrdersGroup}/${accountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => res.data),

  cancel: (
    url: string,
    token: string,
    orderLineCancel: OrderLineCancelBody
  ): Promise<any> =>
    axios
      .postWithRetries<Order>(
        `${url}/v1/orders/lines/cancel`,
        {
          ...orderLineCancel,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => res.data),
  routeToDealer: (
    url: string,
    token: string,
    orderLineRouteToDealer: OrderLineRouteToDealerBody
  ): Promise<any> =>
    axios
      .postWithRetries<Order>(
        `${url}/v1/orders/lines/route-to-dealer`,
        {
          ...orderLineRouteToDealer,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => res.data),
  searchOrders: (
    orderServiceUrl: string,
    token: string,
    body: AllOrdersRequestBody,
    signal?: AbortSignal
  ): Promise<SearchOrderResponse> =>
    axios
      .postWithRetries(
        `${orderServiceUrl}/v1/orders/search`,
        {
          ...body,
          orderCreationDateFrom: formatDate(body?.orderCreationDateFrom),
          orderCreationDateTo: formatDate(body?.orderCreationDateTo),
        },
        signal
          ? { signal, headers: { Authorization: `Bearer ${token}` } }
          : { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response: any) => response.data),

  orderLines: (url: string, token: string, body: OrderLineRequestBody) =>
    axios
      .postWithRetries<OrdersByLineItemResponse>(
        `${url}/v1/orders/lines/search`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => res.data),
  orderLinesV2: (
    url: string,
    token: string,
    body: OrderLineRequestBody,
    signal?: AbortSignal
  ) =>
    axios
      .postWithRetries<OrdersByLineItemResponse>(
        `${url}/v2/orders/lines/search`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
          ...(signal ? { signal } : {}),
        }
      )
      .then((res) => res.data),

  downloadOrders: (
    url: string,
    token: string,
    accountIds: string[],
    locale: string,
    hidePromotionColumn: boolean = false,
    hideDateTimeSubmittedColumn: boolean = false,
    showDateTimeCreatedColumn: boolean = false
  ): Promise<AxiosResponse<BlobResponse>> => {
    const params = [
      ["accountIds", accountIds],
      ["locale", locale],
      ["hidePromotionColumn", hidePromotionColumn],
      ["hideDateTimeSubmittedColumn", hideDateTimeSubmittedColumn],
      ["showDateTimeCreatedColumn", showDateTimeCreatedColumn],
    ]
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return axios.getWithRetries<BlobResponse>(
      `${url}/v1/orders/download?${params}`,
      {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },

  downloadOrderLineItems: (
    url: string,
    token: string,
    accountIds: string[],
    locale: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    axios.getWithRetries<BlobResponse>(
      `${url}/v1/orders/lines/download?accountIds=${accountIds}&locale=${locale}`,
      {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      }
    ),

  getBackOrdersDetails: (
    url: string,
    token: string,
    partNumber: string,
    accountId: string,
    salesOrgId: string
  ): Promise<BackOrdersDetailsResponse> =>
    axios
      .getWithRetries(
        `${url}/v1/orders/back-orders-details/${partNumber}?accountId=${accountId}&salesOrgId=${salesOrgId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response: any) => response.data),

  downloadPendingOrders: (
    url: string,
    token: string,
    accountId: string,
    pendingOrdersGroup: PendingOrdersGroup,
    locale: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    axios.getWithRetries<BlobResponse>(
      `${url}/v1/orders/pending/${pendingOrdersGroup}/${accountId}/download`,
      {
        responseType: "blob",
        params: { locale },
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: EXCEL_MIME_TYPE,
        },
      }
    ),
};

const formatDate = (date: Date | undefined): string | undefined =>
  date ? date.toISOString().split("T")[0] : date;

export default OrderApi;
